import { SelectSortTypeDropdown } from "@components/";
import SC from "./style";
import { Button, Label } from "@InnoUI/";
import {
  Checkbox,
  FormControlLabel,
  List,
  Radio,
  RadioGroup,
  Switch,
} from "@mui/material";
import { addOnLabel, orderOption } from "utils/optionData/utils";
import theme from "@theme/";
import { useState } from "react";
import InnoIcons from "@InnoIcons/";
import { useSelector } from "react-redux";
import { getNestedAccess, readFromLocalStorage } from "utils/helper";
import axios from "axios";

const TSSReport = () => {
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState({
    addon: [],
    sortType: 1,
    sortOrder: 0,
    chart: true,
    includeInReport: true,
    includeHyperLink: true,
    activeJournal: true,
  });

  const currentProject = useSelector(
    (state) => state?.currentProject?.data || [],
  );
  const accessControlData = useSelector(
    (state) => state?.accessControlData?.data || [],
  );
  const canGenerateOssReport =
    getNestedAccess(accessControlData, "canGenerateOssReport") == 1;
  const canGenerateOssSummaryReport =
    getNestedAccess(accessControlData, "canGenerateOssSummaryReport") == 1;
  const canGenerateOssFullReport =
    getNestedAccess(accessControlData, "canGenerateOssFullReport") == 1;
  const canShowReportButtons =
    canGenerateOssReport ||
    canGenerateOssSummaryReport ||
    canGenerateOssFullReport;

  const handleChange = (property, value, isArray = false) => {
    setData((prevData) => {
      if (isArray) {
        const updatedArray = prevData[property]?.includes(value)
          ? prevData[property].filter((id) => id !== value)
          : [...(prevData[property] || []), value];

        return {
          ...prevData,
          [property]: updatedArray,
        };
      }

      return {
        ...prevData,
        [property]: value,
      };
    });
  };

  const {
    REACT_APP_BASE_URL: baseURL,
    REACT_APP_REQUEST_TIMEOUT: timeout,
    REACT_APP_API_KEY,
  } = process.env;

  let accessToken = REACT_APP_API_KEY;
  let userAuth = readFromLocalStorage("userAccessToken");
  let BASE_URL = baseURL || `${window.location.origin}/api`;
  userAuth = userAuth === null ? "" : userAuth;
  const generatePdf = async (reportType) => {
    try {
      setLoading(reportType);
      let endpoint;
      switch (reportType) {
        case "ossReport":
          endpoint = `/v1/report/tssreport`;
          break;
        case "ossSummaryReport":
          endpoint = `/v1/report/tsssummaryreport`;
          break;
        case "ossFullReport":
          endpoint = `/v1/report/fulltssreport`;
          break;
        default:
          throw new Error("Invalid report type");
      }
      const url = BASE_URL + endpoint;
      const requestTimeout = timeout ? parseInt(timeout, 10) * 1000 : 180000;
      const response = await axios.post(
        url,
        {
          projectId: currentProject.id,
          sortType: data.sortType,
          sortOrder: data.sortOrder,
          chart: data.chart,
          includeInReport: data.includeInReport,
          includeHyperLink: data.includeHyperLink,
          activeJournal: data.activeJournal,
        },
        {
          headers: {
            "Content-Type": "application/json",
            api_key: accessToken,
            Authorization: `Bearer ${userAuth}`,
          },
          timeout: requestTimeout,
          responseType: "blob",
        },
      );

      if (response.status === 200) {
        const now = new Date();
        const datePart =
          now.getFullYear().toString() +
          String(now.getMonth() + 1).padStart(2, "0") +
          String(now.getDate()).padStart(2, "0");
        const timePart =
          String(now.getHours()).padStart(2, "0") +
          String(now.getMinutes()).padStart(2, "0");
        const projectName = currentProject?.propertyName || "Report";
        const fileName = `${projectName} - Status Schedule - ${datePart} - ${timePart}.pdf`;

        const blob = new Blob([response.data], {
          type: "application/octet-stream",
        });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.style.display = "none";
        link.href = downloadUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(downloadUrl);
        }, 300);
        setLoading(null);
      } else {
        alert("Failed to generate PDF");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF");
      setLoading(null);
    }
  };

  const handleDownloadSummary = () => {
    alert("In Progress");
  };

  return (
    <SC.ReportContainer>
      {/* Sort Type Section */}
      <SC.LabelContainer>
        <SC.Label>Select Sort Type</SC.Label>
        <SC.Children>
          <SC.Wrapper>
            <SelectSortTypeDropdown
              defaultValue={data.sortType}
              name="sortType"
              onChange={handleChange}
              placeholder="Select type"
            />
          </SC.Wrapper>
        </SC.Children>
      </SC.LabelContainer>

      <SC.LabelContainer>
        <SC.Label>Include chart</SC.Label>
        <SC.Children>
          <SC.SwitchContainer>
            <SC.SegmentedToggle>
              <SC.SegmentedToggleButton
                selected={!data.chart}
                onClick={() => handleChange("chart", false)}
              >
                No
              </SC.SegmentedToggleButton>
              <SC.SegmentedToggleButton
                selected={data.chart}
                onClick={() => handleChange("chart", true)}
              >
                Yes
              </SC.SegmentedToggleButton>
            </SC.SegmentedToggle>
          </SC.SwitchContainer>
        </SC.Children>
      </SC.LabelContainer>

      <SC.LabelContainer>
        <SC.Label>Include additional information</SC.Label>
        <SC.Children>
          <SC.SwitchContainer>
            <SC.SegmentedToggle>
              <SC.SegmentedToggleButton
                selected={!data.includeInReport}
                onClick={() => handleChange("includeInReport", false)}
              >
                No
              </SC.SegmentedToggleButton>
              <SC.SegmentedToggleButton
                selected={data.includeInReport}
                onClick={() => handleChange("includeInReport", true)}
              >
                Yes
              </SC.SegmentedToggleButton>
            </SC.SegmentedToggle>
          </SC.SwitchContainer>
        </SC.Children>
      </SC.LabelContainer>

      <SC.LabelContainer>
        <SC.Label>Exclude Hyperlink</SC.Label>
        <SC.Children>
          <SC.SwitchContainer>
            <SC.SegmentedToggle>
              <SC.SegmentedToggleButton
                selected={!data.includeHyperLink}
                onClick={() => handleChange("includeHyperLink", false)}
              >
                No
              </SC.SegmentedToggleButton>
              <SC.SegmentedToggleButton
                selected={data.includeHyperLink}
                onClick={() => handleChange("includeHyperLink", true)}
              >
                Yes
              </SC.SegmentedToggleButton>
            </SC.SegmentedToggle>
          </SC.SwitchContainer>
        </SC.Children>
      </SC.LabelContainer>

      <SC.LabelContainer>
        <SC.Label>Active Journal</SC.Label>
        <SC.Children>
          <SC.SwitchContainer>
            <SC.SegmentedToggle>
              <SC.SegmentedToggleButton
                selected={!data.activeJournal}
                onClick={() => handleChange("activeJournal", false)}
              >
                No
              </SC.SegmentedToggleButton>
              <SC.SegmentedToggleButton
                selected={data.activeJournal}
                onClick={() => handleChange("activeJournal", true)}
              >
                Yes
              </SC.SegmentedToggleButton>
            </SC.SegmentedToggle>
          </SC.SwitchContainer>
        </SC.Children>
      </SC.LabelContainer>

      {/* Order Section */}
      <SC.LabelContainer>
        <SC.Label>Order</SC.Label>
        <SC.Children>
          <SC.SwitchContainer>
            <SC.SegmentedToggle wide>
              <SC.SegmentedToggleButton
                selected={data.sortOrder === 1}
                onClick={() => handleChange("sortOrder", 1)}
              >
                Ascending
              </SC.SegmentedToggleButton>
              <SC.SegmentedToggleButton
                selected={data.sortOrder === 2}
                onClick={() => handleChange("sortOrder", 2)}
              >
                Descending
              </SC.SegmentedToggleButton>
            </SC.SegmentedToggle>
          </SC.SwitchContainer>
        </SC.Children>
      </SC.LabelContainer>

      {canShowReportButtons && (
        <SC.ResponsiveButtonContainer>
          {canGenerateOssReport && (
            <Button
              caption={loading == "ossReport" ? "Downloading..." : "Report"}
              onClick={() => generatePdf("ossReport")}
              disabled={loading === "ossReport"}
              color={"white"}
              fontSize="12px"
              leftIcon={
                <InnoIcons.DownloadIcon
                  sx={{
                    fontSize: "16px",
                    color: "white",
                  }}
                />
              }
              style={{
                marginBottom: 8,
                background: "#33691E",
                color: theme.colors.white,
                minWidth: "200px",
                height: "30px",
                borderRadius: "5px",
                opacity: "1",
              }}
            />
          )}
          {canGenerateOssSummaryReport && (
            <Button
              caption={
                loading == "ossSummaryReport"
                  ? "Downloading..."
                  : "Report Summary"
              }
              onClick={() => generatePdf("ossSummaryReport")}
              disabled={loading === "ossSummaryReport"}
              color={"white"}
              fontSize="12px"
              leftIcon={
                <InnoIcons.DownloadIcon
                  sx={{
                    fontSize: "16px",
                    color: "white",
                  }}
                />
              }
              style={{
                marginBottom: 8,
                background: "#33691E",
                color: theme.colors.white,
                minWidth: "200px",
                height: "30px",
                borderRadius: "5px",
                opacity: "1",
              }}
            />
          )}
          {canGenerateOssFullReport && (
            <Button
              caption={
                loading == "ossFullReport" ? "Downloading..." : "Full Report"
              }
              onClick={() => generatePdf("ossFullReport")}
              disabled={loading === "ossFullReport"}
              color={"white"}
              fontSize="12px"
              leftIcon={
                <InnoIcons.DownloadIcon
                  sx={{
                    fontSize: "16px",
                    color: "white",
                  }}
                />
              }
              style={{
                marginBottom: 8,
                background: "#33691E",
                color: theme.colors.white,
                minWidth: "200px",
                height: "30px",
                borderRadius: "5px",
                opacity: "1",
              }}
            />
          )}
        </SC.ResponsiveButtonContainer>
      )}
    </SC.ReportContainer>
  );
};

export default TSSReport;
